import { getprofile } from '@api/authentication';
import { generateOtp, verifyWhatsappOtp } from '@api/verification';
import { SignIn } from '@components/authentication/signin';
import { SignUp } from '@components/authentication/signup';
import { TOtp, TIdentity } from '@components/types';
import { profileState } from '@recoil/atoms';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Input, message } from 'antd';
import { ChevronLeft, Loader, Phone, ShieldCheck, X } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import OtpModal from '@components/spa/otpmodal';

interface Props {
  showModal: boolean;
  setShowModal: Function;
}

type AuthView = 'login' | 'signup' | 'mobile';

const AuthPopup: React.FC<Props> = ({ showModal, setShowModal }) => {
  const [view, setView] = useState<AuthView>('login');
  const [, setUser] = useRecoilState<TIdentity>(profileState);

  // ── Mobile OTP state ────────────────────────────────────────────────────
  const [mobilePhone,   setMobilePhone]   = useState('');
  const [otpId,         setOtpId]         = useState(0);
  const [otp,           setOtp]           = useState(0);
  const [otpMsg,        setOtpMsg]        = useState('');
  const [otpLoading,    setOtpLoading]    = useState(false);
  const [openOtpModal,  setOpenOtpModal]  = useState(false);

  const onCloseFun = () => {
    setShowModal(false);
    setView('login');
    setMobilePhone('');
    setOtpMsg('');
    setOtpId(0);
    setOtp(0);
  };

  const sendOtp = () => {
    const cleaned = mobilePhone.replace(/\D/g, '');
    if (cleaned.length !== 10) {
      setOtpMsg('Please enter a valid 10-digit mobile number');
      return;
    }
    if (/^[0-5]/.test(cleaned)) {
      setOtpMsg('Please enter a valid Indian mobile number');
      return;
    }
    setOtpLoading(true);
    generateOtp({ key: cleaned, type: 'm' })
      .then((res: TOtp) => {
        if (res?.success) {
          setOtpMsg(res?.msg || 'OTP sent!');
          setOtpId(res.otp_id);
          setOpenOtpModal(true);
        } else {
          setOtpMsg('Could not send OTP. Try again.');
        }
      })
      .catch(() => setOtpMsg('Could not send OTP. Try again.'))
      .finally(() => setOtpLoading(false));
  };

  const verifyOtp = () => {
    verifyWhatsappOtp(otpId, otp)
      .then((res: any) => {
        if (res?.access_token?.length > 0) {
          localStorage.setItem('token', res.access_token);
          getprofile().then((profile: TIdentity) => {
            setUser(profile);
            localStorage.setItem('profile', JSON.stringify(profile));
            message.success('Logged in successfully!');
            setOpenOtpModal(false);
            onCloseFun();
          });
        } else {
          message.error('Invalid OTP. Please try again.');
        }
      })
      .catch(() => message.error('OTP verification failed.'));
  };

  // ── Header labels ────────────────────────────────────────────────────────
  const headerTitle = view === 'signup' ? 'Enter Details'
    : view === 'mobile' ? 'Login with Mobile'
    : 'Login';

  const showBack = view === 'signup' || view === 'mobile';

  // ── Shared header ────────────────────────────────────────────────────────
  const ModalHeader = () => (
    <div className="flex items-center justify-between px-5 pt-4 pb-2">
      <div className="flex items-center gap-2">
        {showBack && (
          <button
            onClick={() => setView('login')}
            className="flex items-center justify-center w-7 h-7 rounded-lg hover:bg-neutral-100 transition-colors text-neutral-500"
          >
            <ChevronLeft size={18} />
          </button>
        )}
        <span className="text-base font-bold text-neutral-800 font-manrope">{headerTitle}</span>
      </div>
      <button
        onClick={onCloseFun}
        className="flex items-center justify-center w-7 h-7 rounded-lg hover:bg-neutral-100 transition-colors text-neutral-400"
      >
        <X size={16} />
      </button>
    </div>
  );

  // ── Shared body ──────────────────────────────────────────────────────────
  const ModalBody = () => (
    <GoogleOAuthProvider clientId="624666456033-814o92j1gov9dq125a20b3l2c50r1k6j.apps.googleusercontent.com">
      {view === 'signup' && (
        <SignUp type="user" setIsSignUp={() => setView('login')} />
      )}
      {view === 'login' && (
        <>
          <SignIn setIsSignUp={() => setView('signup')} setShowModel={setShowModal} />
          <div className="flex items-center gap-3 my-3">
            <div className="flex-1 h-px bg-neutral-200" />
            <span className="text-xs text-neutral-400 font-medium font-manrope">OR</span>
            <div className="flex-1 h-px bg-neutral-200" />
          </div>
          <button
            type="button"
            onClick={() => setView('mobile')}
            className="flex items-center justify-center gap-2 w-full border border-neutral-300 shadow-md rounded-xl py-3 text-sm font-semibold text-neutral-700 hover:bg-neutral-100 transition-colors mb-3 font-manrope cursor-pointer"
          >
            <Phone size={16} className="text-neutral-500" />
            Login with Mobile OTP
          </button>
        </>
      )}
      {view === 'mobile' && (
        <div className="flex flex-col gap-4 py-2 pb-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide font-manrope">
              Mobile Number
            </label>
            <Input
              size="large"
              type="tel"
              placeholder="Enter 10-digit mobile number"
              maxLength={10}
              value={mobilePhone}
              prefix={
                <span className="flex items-center gap-1.5 pr-2 border-r border-neutral-200 mr-1">
                  <Phone size={13} className="text-neutral-400" />
                  <span className="text-sm text-neutral-500 font-medium">+91</span>
                </span>
              }
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                setMobilePhone(digits);
                setOtpMsg('');
                if (digits.length > 0 && digits.length < 10) {
                  setOtpMsg(`${10 - digits.length} more digit${10 - digits.length > 1 ? 's' : ''} needed`);
                }
              }}
              onKeyDown={(e) => {
                const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
                if (allowed.includes(e.key)) return;
                if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) return;
                if (!/^\d$/.test(e.key)) e.preventDefault();
                if (mobilePhone.length >= 10 && !allowed.includes(e.key)) e.preventDefault();
              }}
              onPaste={(e) => {
                e.preventDefault();
                const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 10);
                setMobilePhone(pasted);
                setOtpMsg('');
              }}
              status={otpMsg && mobilePhone.length > 0 && mobilePhone.length < 10 ? 'warning' : ''}
              className="rounded-xl"
            />
            <div className="flex items-center justify-between mt-0.5">
              {otpMsg
                ? <p className={`text-xs font-manrope ${mobilePhone.length === 10 ? 'text-neutral-500' : 'text-amber-500'}`}>{otpMsg}</p>
                : <span />
              }
              <p className="text-xs text-neutral-400 font-manrope">{mobilePhone.length}/10</p>
            </div>
          </div>
          <button
            type="button"
            onClick={sendOtp}
            disabled={otpLoading || mobilePhone.length !== 10}
            className="flex items-center justify-center gap-2 w-full bg-brand-500 text-white rounded-xl py-3 text-sm font-bold font-manrope transition-all hover:opacity-90 disabled:opacity-50"
          >
            {otpLoading
              ? <><Loader size={15} className="animate-spin" /> Sending OTP…</>
              : <><ShieldCheck size={15} /> Verify with OTP</>
            }
          </button>
        </div>
      )}
    </GoogleOAuthProvider>
  );

  return (
    <>
      {showModal && (
        <>
          {/* backdrop */}
          <div className="fixed inset-0 z-40 bg-black/40" onClick={onCloseFun} />

          {/* ── Mobile: bottom sheet ──────────────────────────────────────── */}
          <div
            className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl shadow-2xl font-manrope"
            onClick={(e) => e.stopPropagation()}
          >
            {/* drag handle */}
            <div className="flex justify-center pt-3">
              <div className="w-10 h-1 rounded-full bg-neutral-200" />
            </div>
            <ModalHeader />
            <div className="px-5 pb-8 overflow-y-auto max-h-[80vh]">
              <ModalBody />
            </div>
          </div>

          {/* ── Desktop: centered modal ───────────────────────────────────── */}
          <div
            className="hidden md:flex justify-center items-center fixed inset-0 z-50 p-4"
            onClick={onCloseFun}
          >
            <div
              className="relative bg-neutral-50 rounded-2xl shadow-xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader />
              <div className="px-5 pb-6">
                <ModalBody />
              </div>
            </div>
          </div>

          {/* OTP Modal */}
          <OtpModal
            showModal={openOtpModal}
            setShowModal={setOpenOtpModal}
            ActionFun={verifyOtp}
            closeFunction={() => { setOpenOtpModal(false); setOtpId(0); setOtp(0); setOtpMsg(''); }}
            otp={otp}
            setOtp={setOtp}
            retryfunction={sendOtp}
            phone={mobilePhone}
            message={otpMsg}
          />
        </>
      )}
    </>
  );
};

export default AuthPopup;